import React, { useState } from 'react';
import { useDialogsContext } from '../../contexts/Dialog';
import { DialogNames } from '../../Types';
import { useEventTrackerContext } from '../../contexts/EventTracker';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import DialogHeader from '../BuildingBlocks/Dialogs/DialogStyleOne/DialogHeader';
import DialogContent from '../BuildingBlocks/Dialogs/DialogStyleOne/DialogContent';
import ButtonSelector from '../BuildingBlocks/Button/ButtonSelector';

const CHARACTER_LIMIT = 255;

const CustomerRejectionReasons = {
  CostToHigh: { value: 1, text: 'Cost of capital too high' },
  ScheduleNotGood: {
    value: 2,
    text: "Funds delivery schedule doesn't suit my needs",
  },
  ContractTerms: { value: 3, text: 'Contract terms' },
  PreferDifferentSolution: { value: 4, text: 'Prefer different solution' },
  NotNeeded: { value: 5, text: 'No longer need capital' },
  Else: { value: 6, text: 'Something else' },
};

function getRejectReasonText(reasonNum: number) {
  return Object.values(CustomerRejectionReasons).find(
    (e) => e.value === reasonNum,
  ).text;
}

const useStyles = makeStyles((theme: any) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingRight: theme.spacing(6.25),
    paddingLeft: theme.spacing(6.25),
    color: theme.palette.grey[600],
  },
  dialogHeader: { textAlign: 'center', fontSize: '22px', fontWeight: 'bold' },
  dialogContent: { textAlign: 'center', fontSize: '20px' },
  dialogActionButton: {
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    margin: 0,
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    borderRadius: '4px',
  },
  pages: { width: '100%' },
  align: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  button: {
    borderRadius: '4px',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    width: 300,
  },
  cancelButton: {
    backgroundColor: theme.palette.brand.blueLight,
  },
  cancelLine: { color: theme.palette.error.main },
  bold: { fontWeight: 900 },
  textArea: {
    boxSizing: 'inherit',
    fontSize: 'large',
    resize: 'none',
    backgroundColor: theme.palette.brand.grey,
    borderRadius: '4px',
  },
  textAreaOuter: {
    marginBottom: theme.spacing(4),
    minWidth: '100%',
    maxWidth: 'inherit',
  },
}));

function TellUsWhy({ reason, setReason, goForward, close }) {
  const classes = useStyles();

  return (
    <div className={classes.content}>
      <DialogHeader
        className={classes.dialogHeader}
        title="We’re sorry your custom Growth Plan did not meet your expectations."
      />
      <DialogContent className={classes.dialogContent}>
        Please tell us briefly why you chose not to activate your Growth Plan.
      </DialogContent>
      <DialogActions className={classes.align}>
        {Object.values(CustomerRejectionReasons).map(({ value, text }) => (
          <ButtonSelector
            key={text}
            name={text}
            className={classes.dialogActionButton}
            selected={value === reason}
            onClick={() => setReason(value)}
          />
        ))}
      </DialogActions>
      <Button
        color="primary"
        variant="contained"
        className={`${classes.button}`}
        onClick={() => goForward()}
        disabled={!reason}
      >
        <b>Decline {'&'} Send Feedback</b>
      </Button>

      <Button
        variant="contained"
        color="primary"
        className={classes.button}
        onClick={() => close()}
      >
        <b>I didn’t mean to Decline! Take me back</b>
      </Button>
    </div>
  );
}

function LeaveFeedback({ plan, reason, close, rejectEditedPlan, trackEvent }) {
  const classes = useStyles();
  const [reviewData, setExtraText] = useState('');

  return (
    <div className={classes.content}>
      <DialogHeader
        className={classes.dialogHeader}
        title="Your Plan will be archived soon."
      />
      <DialogContent className={classes.dialogContent}>
        Finally, what would you like our team to know? How could we improve your
        plan, and any future Growth Plans we may issue?
      </DialogContent>
      <TextField
        onChange={(e) => setExtraText(e.target.value)}
        value={reviewData}
        name="message"
        placeholder="Required: Provide more details"
        id="message"
        inputProps={{
          maxLength: CHARACTER_LIMIT,
        }}
        rows={8}
        rowsMax={8}
        multiline
        variant="outlined"
        fullWidth
        helperText={`${reviewData?.length ?? 0}/${CHARACTER_LIMIT}`}
        className={classes.textAreaOuter}
        InputProps={{ className: classes.textArea }}
      />
      <Button
        color="primary"
        variant="contained"
        className={`${classes.button}`}
        disabled={reviewData.length <= 0}
        onClick={() => {
          close();
          trackEvent({
            event: 'user_rejected_plan',
            changeRequestId: plan.opcId,
            reason: getRejectReasonText(reason),
          });

          rejectEditedPlan(plan, reason, reviewData);
        }}
      >
        <b>Send</b>
      </Button>
    </div>
  );
}

export default function RejectEditedPlanDialog({ plan, rejectEditedPlan }) {
  const classes = useStyles();
  const [reason, setReason] = useState(null);
  const dialog: any = useDialogsContext();
  const { trackEvent } = useEventTrackerContext();

  const [activeStep, setActiveStep] = useState(0);

  function goForward() {
    setActiveStep((activeStep) => activeStep + 1);
  }

  const close = () => {
    dialog.close(DialogNames.RejectEditedPlan);
    setActiveStep(0);
    setReason(null);
  };

  const pages = [
    <TellUsWhy
      reason={reason}
      setReason={setReason}
      goForward={goForward}
      close={close}
    />,
    <LeaveFeedback
      reason={reason}
      plan={plan}
      close={close}
      rejectEditedPlan={rejectEditedPlan}
      trackEvent={trackEvent}
    />,
  ];

  return (
    <Dialog open={dialog.rejectEditedPlan}>
      <DialogContent className={classes.content}>
        <div className={classes.pages}> {pages[activeStep]}</div>
      </DialogContent>
    </Dialog>
  );
}
